import { parseAsString, useQueryStates } from 'nuqs'
import { createContext, type PropsWithChildren, useContext } from 'react'

import { type PanoramaImage, usePanorama } from '@state/panorama'

interface PanoramaImageContextType {
  currentImage: PanoramaImage | undefined
  previousImage: PanoramaImage | undefined
  selectImage: (image: PanoramaImage) => void
  resetImage: () => void
}

const PanoramaImageContext = createContext<PanoramaImageContextType | undefined>(undefined)

export function PanoramaImageProvider({ children }: PropsWithChildren) {
  const { data: panoramaImages } = usePanorama()
  const [queryState, setQueryState] = useQueryStates({ image: parseAsString, prev: parseAsString })
  const { image: currentImageId, prev: previousImageId } = queryState

  const currentImage = panoramaImages?.find(img => img.id === currentImageId)
  const previousImage = panoramaImages?.find(img => img.id === previousImageId)

  function selectImage(image: PanoramaImage) {
    setQueryState((old) => ({
      prev: old.image ?? old.prev,
      image: image.id,
    }))
  }

  function resetImage() {
    setQueryState({ prev: null, image: null })
  }

  return (
    <PanoramaImageContext.Provider value={{
      currentImage,
      previousImage,
      selectImage,
      resetImage,
    }}>
      {children}
    </PanoramaImageContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePanoramaContext() {
  const context = useContext(PanoramaImageContext)

  if (context === undefined) {
    throw new Error('usePanoramaImage must be used within a PanoramaImageProvider')
  }

  return context
}

